import React, { useState, useEffect, useCallback, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { parseCustomerShippingAddress } from '../../utils/customerShippingAddress';
import { CHECKOUT_COUNTRIES } from '../../constants/checkoutCountries';

/** Both pickup and drop-off happen on the same weekly Friday 8am–8pm run — this is that date. */
function nextFridayLabel() {
  const now = new Date();
  const day = now.getDay(); // 0 = Sun ... 5 = Fri, 6 = Sat
  let daysUntilFriday = (5 - day + 7) % 7;
  if (daysUntilFriday === 0 && now.getHours() >= 20) daysUntilFriday = 7; // past this Friday's 8pm cutoff
  const friday = new Date(now);
  friday.setDate(now.getDate() + daysUntilFriday);
  return friday.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

const T = {
  ink: '#1f2937',
  inkLight: '#6b7280',
  border: 'rgba(17,24,39,0.08)',
  pink: '#ec4899',
  purple: '#8b5cf6',
  gradient: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
  green: '#15803d',
  greenPale: '#dcfce7',
  red: '#b91c1c',
  redPale: '#fee2e2',
  grayPale: '#f3f4f6',
};

const card = { background: '#fff', color: T.ink, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 18px', marginBottom: 16 };
const input = { padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const primaryBtn = { background: T.gradient, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const ghostBtn = { background: '#fff', color: T.ink, border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const stepBtn = { width: 28, height: 28, borderRadius: 8, border: `1px solid ${T.border}`, background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: '1' };

let stripePromiseCache = null;
async function getStripePromise() {
  if (stripePromiseCache) return stripePromiseCache;
  try {
    const backendUrl = API_BASE_URL.replace(/\/api\/v1$/, '');
    const businessId = process.env.REACT_APP_BUSINESS_ID || '';
    const res = await fetch(`${backendUrl}/api/v1/business/stripe-public${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`);
    const data = await res.json().catch(() => ({}));
    const key = (data.publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
    stripePromiseCache = key ? loadStripe(key) : null;
  } catch {
    const fallback = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
    stripePromiseCache = fallback ? loadStripe(fallback) : null;
  }
  return stripePromiseCache;
}

/** Inner form — must render inside <Elements> so useStripe/useElements resolve. */
const PayForm = ({ onPaid, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    setSubmitting(false);
    if (confirmError) {
      setError(confirmError.message || 'Payment failed');
      return;
    }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      onPaid();
    }
  };

  return (
    <div>
      <PaymentElement />
      {error && <p style={{ color: T.red, fontSize: 13, marginTop: 10 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button type="button" style={primaryBtn} onClick={handlePay} disabled={submitting || !stripe}>
          {submitting ? 'Processing…' : 'Pay now'}
        </button>
        <button type="button" style={ghostBtn} onClick={onCancel} disabled={submitting}>Cancel</button>
      </div>
    </div>
  );
};

const SecretShopTab = ({ showAccessTabHint = true } = {}) => {
  const { getAuthHeaders, customer } = useCustomerAuth();
  const savedAddress = parseCustomerShippingAddress(customer?.shippingAddress);

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [minQualifyingPrice, setMinQualifyingPrice] = useState(100);
  const [dropoffSurcharge, setDropoffSurcharge] = useState(5);
  const [myOrders, setMyOrders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [openVideoIndex, setOpenVideoIndex] = useState(null);
  const videoScrollerRef = useRef(null);

  const [quantities, setQuantities] = useState({}); // { [productId]: number }
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState('pickup');
  const [address, setAddress] = useState({ street: '', apartment: '', city: '', state: '', postcode: '', country: 'Australia' });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  const [messagingOrderId, setMessagingOrderId] = useState(null);
  const [orderMessages, setOrderMessages] = useState([]);
  const [loadingOrderMessages, setLoadingOrderMessages] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const secretShop = API_ENDPOINTS.secretShop;

  const loadAccess = useCallback(async () => {
    try {
      const res = await fetch(secretShop.myAccess, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data?.success) {
        setProducts(data.data.products || []);
        if (data.data.minQualifyingPrice) setMinQualifyingPrice(data.data.minQualifyingPrice);
        if (data.data.dropoffSurcharge != null) setDropoffSurcharge(data.data.dropoffSurcharge);
      }
    } catch {
      // Access check failing is non-fatal — the redeem box still works.
    }
  }, [getAuthHeaders, secretShop]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await fetch(secretShop.myOrders, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data?.success) setMyOrders(data.data || []);
    } catch {
      // Non-fatal.
    }
  }, [getAuthHeaders, secretShop]);

  const loadVideos = useCallback(async () => {
    try {
      const res = await fetch(secretShop.myVideos, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data?.success) setVideos(data.data || []);
    } catch {
      // Non-fatal.
    }
  }, [getAuthHeaders, secretShop]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAccess(), loadOrders(), loadVideos()]).finally(() => setLoading(false));
  }, [loadAccess, loadOrders, loadVideos]);

  useEffect(() => {
    if (openVideoIndex !== null && videoScrollerRef.current) {
      const el = videoScrollerRef.current;
      el.scrollLeft = openVideoIndex * el.clientWidth;
    }
  }, [openVideoIndex]);

  const deliveryDateLabel = nextFridayLabel();

  const hasQualifyingItemInCart = products.some(
    (p) => p.price >= minQualifyingPrice && (quantities[p.id] || 0) > 0
  );
  const visibleProducts = products.filter((p) => p.price >= minQualifyingPrice || hasQualifyingItemInCart);
  const cartItems = visibleProducts
    .filter((p) => (quantities[p.id] || 0) > 0)
    .map((p) => ({ product: p, quantity: quantities[p.id] }));
  const subtotal = cartItems.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  const surcharge = deliveryMode === 'dropoff' && cartItems.length > 0 ? dropoffSurcharge : 0;
  const total = subtotal + surcharge;

  const setQuantity = (productId, qty) => {
    setQuantities((prev) => ({ ...prev, [productId]: Math.max(0, Math.floor(qty) || 0) }));
  };

  const openCheckout = () => {
    setAddress(
      savedAddress
        ? {
            street: savedAddress.street,
            apartment: savedAddress.apartment,
            city: savedAddress.city,
            state: savedAddress.state,
            postcode: savedAddress.postalCode,
            country: CHECKOUT_COUNTRIES.find((c) => c.code === savedAddress.country)?.label || 'Australia',
          }
        : { street: '', apartment: '', city: '', state: '', postcode: '', country: 'Australia' }
    );
    setDeliveryMode('pickup');
    setOrderError('');
    setClientSecret(null);
    setCheckoutOpen(true);
  };

  const handleCreateOrder = async () => {
    setPlacingOrder(true);
    setOrderError('');
    try {
      const res = await fetch(secretShop.orders, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cartItems.map((i) => ({ secretProductId: i.product.id, quantity: i.quantity })),
          deliveryMode,
          dropoffAddress: deliveryMode === 'dropoff' ? address : undefined,
        }),
      });
      const data = await res.json();
      if (!data?.success) {
        setOrderError(data?.error || 'Could not start this order.');
        setPlacingOrder(false);
        return;
      }
      if (data.data.free) {
        handlePaid();
        return;
      }
      const promise = await getStripePromise();
      setStripePromise(promise);
      setClientSecret(data.data.clientSecret);
    } catch (err) {
      setOrderError(err.message || 'Could not start this order.');
    } finally {
      setPlacingOrder(false);
    }
  };

  const handlePaid = () => {
    setCheckoutOpen(false);
    setClientSecret(null);
    setQuantities({});
    loadOrders();
  };

  const handleToggleOrderMessages = async (orderId) => {
    if (messagingOrderId === orderId) {
      setMessagingOrderId(null);
      return;
    }
    setMessagingOrderId(orderId);
    setMessageDraft('');
    setLoadingOrderMessages(true);
    try {
      const res = await fetch(API_ENDPOINTS.secretShop.orderMessages(orderId), { headers: getAuthHeaders() });
      const data = await res.json();
      setOrderMessages(data?.success && data.data ? data.data : []);
    } catch {
      setOrderMessages([]);
    } finally {
      setLoadingOrderMessages(false);
    }
  };

  const handleSendOrderMessage = async () => {
    if (!messagingOrderId || !messageDraft.trim()) return;
    setSendingMessage(true);
    try {
      const res = await fetch(API_ENDPOINTS.secretShop.orderMessages(messagingOrderId), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageDraft.trim() }),
      });
      const data = await res.json();
      if (data?.success) {
        setMessageDraft('');
        const refreshed = await fetch(API_ENDPOINTS.secretShop.orderMessages(messagingOrderId), { headers: getAuthHeaders() });
        const refreshedData = await refreshed.json();
        setOrderMessages(refreshedData?.success && refreshedData.data ? refreshedData.data : []);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) return <p style={{ color: T.inkLight }}>Loading…</p>;

  return (
    <div>
      {products.length === 0 && (
        <div style={card}>
          <p style={{ color: T.inkLight, fontSize: 13, margin: 0 }}>
            {showAccessTabHint ? (
              <>Nothing unlocked yet — got a code? Enter it under the <strong>Access</strong> tab.</>
            ) : (
              'Nothing unlocked yet — reach out if you were expecting access.'
            )}
          </p>
        </div>
      )}

      {products.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: T.ink }}>Unlocked for you</div>
          <p style={{ fontSize: 12, color: T.inkLight, margin: '0 0 12px 0' }}>
            Set a quantity for anything you want, then check out once — pickup is free, or we can drop it off to your
            address for a small delivery fee.
          </p>
          {visibleProducts.map((p) => {
            const soldOut = p.stockQuantity === 0;
            const atStockLimit = p.stockQuantity != null && (quantities[p.id] || 0) >= p.stockQuantity;
            return (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderTop: `1px solid ${T.border}`, padding: '12px 0', opacity: soldOut ? 0.6 : 1 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: T.ink }}>{p.name}</div>
                  {p.description && <div style={{ fontSize: 12, color: T.inkLight, marginTop: 2 }}>{p.description}</div>}
                  <div style={{ fontSize: 13, marginTop: 4, color: T.ink }}>${p.price.toFixed(2)} each</div>
                  {soldOut ? (
                    <div style={{ fontSize: 12, color: T.red, marginTop: 2, fontWeight: 600 }}>Sold out</div>
                  ) : p.stockQuantity != null && p.stockQuantity <= 5 ? (
                    <div style={{ fontSize: 12, color: T.red, marginTop: 2 }}>Only {p.stockQuantity} left</div>
                  ) : null}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button type="button" style={stepBtn} onClick={() => setQuantity(p.id, (quantities[p.id] || 0) - 1)} disabled={!(quantities[p.id] > 0)}>−</button>
                  <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 600, color: T.ink }}>{quantities[p.id] || 0}</span>
                  <button type="button" style={stepBtn} onClick={() => setQuantity(p.id, (quantities[p.id] || 0) + 1)} disabled={soldOut || atStockLimit}>+</button>
                </div>
              </div>
            );
          })}
          {visibleProducts.length < products.length && (
            <p style={{ fontSize: 12, color: T.inkLight, marginTop: 12, marginBottom: 0 }}>
              Add a ${minQualifyingPrice}+ item to unlock the rest.
            </p>
          )}

          {cartItems.length > 0 && !checkoutOpen && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: T.ink }}>Subtotal: ${subtotal.toFixed(2)}</div>
              <button type="button" style={primaryBtn} onClick={openCheckout}>Checkout →</button>
            </div>
          )}

          {checkoutOpen && (
            <div style={{ marginTop: 14, background: T.grayPale, color: T.ink, borderRadius: 10, padding: 14 }}>
              {!clientSecret ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: T.ink }}>Your order</div>
                  {cartItems.map((i) => (
                    <div key={i.product.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, color: T.ink }}>
                      <span>{i.product.name} × {i.quantity}</span>
                      <span>${(i.product.price * i.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 10, marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: T.ink }}>Delivery</label>
                    <div style={{ display: 'flex', gap: 14 }}>
                      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, color: T.ink }}>
                        <input type="radio" checked={deliveryMode === 'pickup'} onChange={() => setDeliveryMode('pickup')} />
                        Pickup (free) — {deliveryDateLabel}, 8am–8pm. Location shared with everyone who ordered pickup that week.
                      </label>
                      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, color: T.ink }}>
                        <input type="radio" checked={deliveryMode === 'dropoff'} onChange={() => setDeliveryMode('dropoff')} />
                        Drop-off to your address (+${dropoffSurcharge.toFixed(2)}) — left in your mailbox on {deliveryDateLabel}.
                      </label>
                    </div>
                  </div>
                  {deliveryMode === 'dropoff' && (
                    <div style={{ marginBottom: 10 }}>
                      <p style={{ fontSize: 12, color: T.inkLight, margin: '0 0 8px 0' }}>
                        {savedAddress
                          ? "We'll deliver to the mailbox/address saved on your profile — edit below if this order should go elsewhere."
                          : "Enter the mailbox/address we should deliver to."}
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <input style={{ ...input, color: T.ink, gridColumn: '1 / -1' }} placeholder="Street address" value={address.street}
                          onChange={(e) => setAddress({ ...address, street: e.target.value })} />
                        <input style={{ ...input, color: T.ink, gridColumn: '1 / -1' }} placeholder="Unit / apartment (optional)" value={address.apartment}
                          onChange={(e) => setAddress({ ...address, apartment: e.target.value })} />
                        <input style={{ ...input, color: T.ink }} placeholder="City" value={address.city}
                          onChange={(e) => setAddress({ ...address, city: e.target.value })} />
                        <input style={{ ...input, color: T.ink }} placeholder="State" value={address.state}
                          onChange={(e) => setAddress({ ...address, state: e.target.value })} />
                        <input style={{ ...input, color: T.ink }} placeholder="Postcode" value={address.postcode}
                          onChange={(e) => setAddress({ ...address, postcode: e.target.value })} />
                      </div>
                    </div>
                  )}
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: T.ink }}>Total: ${total.toFixed(2)}</div>
                  {orderError && <p style={{ color: T.red, fontSize: 13 }}>{orderError}</p>}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" style={primaryBtn} onClick={handleCreateOrder} disabled={placingOrder}>
                      {placingOrder ? 'Preparing…' : 'Continue to payment'}
                    </button>
                    <button type="button" style={ghostBtn} onClick={() => setCheckoutOpen(false)}>Back</button>
                  </div>
                </>
              ) : stripePromise ? (
                <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                  <PayForm onPaid={handlePaid} onCancel={() => setCheckoutOpen(false)} />
                </Elements>
              ) : (
                <p style={{ color: T.red, fontSize: 13 }}>Payments aren't set up for this shop yet.</p>
              )}
            </div>
          )}
        </div>
      )}

      <div style={card}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: T.ink }}>Your orders</div>
        {myOrders.length === 0 ? (
          <p style={{ color: T.inkLight, fontSize: 13, margin: 0 }}>No orders yet.</p>
        ) : (
          myOrders.map((o) => (
            <div key={o.id} style={{ borderTop: `1px solid ${T.border}`, padding: '10px 0', fontSize: 13, color: T.ink }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{o.secretProduct.name} × {o.quantity}</div>
                  <div style={{ color: T.inkLight, marginTop: 2 }}>
                    {o.deliveryMode === 'pickup' ? 'Pickup' : 'Drop-off'} · ${o.amount.toFixed(2)} · {o.paymentStatus}
                    {o.fulfilledAt ? ' · Delivered' : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleOrderMessages(o.id)}
                  style={{ ...ghostBtn, padding: '6px 12px', fontSize: 12, whiteSpace: 'nowrap' }}
                >
                  {messagingOrderId === o.id ? 'Close' : '💬 Message'}
                </button>
              </div>
              {messagingOrderId === o.id && (
                <div style={{ marginTop: 10, background: T.grayPale, borderRadius: 10, padding: 12 }}>
                  <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 10 }}>
                    {loadingOrderMessages ? (
                      <div style={{ color: T.inkLight, fontSize: 12 }}>Loading…</div>
                    ) : orderMessages.length === 0 ? (
                      <div style={{ color: T.inkLight, fontSize: 12 }}>No messages yet.</div>
                    ) : (
                      orderMessages.map((m) => (
                        <div
                          key={m.id}
                          style={{
                            display: 'flex',
                            justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              maxWidth: '80%',
                              background: m.role === 'user' ? T.gradient : '#fff',
                              color: m.role === 'user' ? '#fff' : T.ink,
                              border: m.role === 'user' ? 'none' : `1px solid ${T.border}`,
                              borderRadius: 10,
                              padding: '7px 11px',
                              fontSize: 13,
                            }}
                          >
                            <div>{m.content}</div>
                            <div style={{ fontSize: 10, marginTop: 2, opacity: 0.75 }}>
                              {new Date(m.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      style={{ ...input, flex: 1 }}
                      placeholder="Type a message…"
                      value={messageDraft}
                      onChange={(e) => setMessageDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !sendingMessage) handleSendOrderMessage();
                      }}
                    />
                    <button type="button" style={primaryBtn} onClick={handleSendOrderMessage} disabled={sendingMessage || !messageDraft.trim()}>
                      {sendingMessage ? '…' : 'Send'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {videos.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: T.ink }}>Learn more</div>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
            {videos.map((v, i) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setOpenVideoIndex(i)}
                style={{
                  flex: '0 0 auto',
                  width: 120,
                  height: 80,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  background: '#000',
                  cursor: 'pointer',
                  padding: 0,
                  overflow: 'hidden',
                  position: 'relative',
                }}
                title={v.title || 'Video'}
              >
                {v.thumbnailUrl ? (
                  <img src={v.thumbnailUrl} alt={v.title || 'Video'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ color: '#fff', fontSize: 24 }}>▶</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {openVideoIndex !== null && (
        <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <button
            type="button"
            onClick={() => setOpenVideoIndex(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1001,
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
          <div
            ref={videoScrollerRef}
            style={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', width: '100%', height: '100%' }}
          >
            {videos.map((v) => (
              <div
                key={v.id}
                style={{ flex: '0 0 100%', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
              >
                <video src={v.videoUrl} controls style={{ maxWidth: '100%', maxHeight: '85%' }} />
                {v.title && <div style={{ color: '#fff', fontSize: 13, marginTop: 12, textAlign: 'center', padding: '0 20px' }}>{v.title}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SecretShopTab;
